import React, { useState } from "react";
import { Toaster, toast } from "sonner";
import UserLayout from "../components/UserLayout";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { api, extractError } from "../lib/api";
import {
  UserCircle,
  EnvelopeSimple,
  Buildings,
  ShieldCheck,
  Calendar,
  Lock,
  Warning,
  CheckCircle,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-AT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function ReadOnlyField({ icon: Icon, label, value, testId }) {
  return (
    <div className="border border-ink-200 bg-warm-50 rounded-sm p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1.5">
        <Icon size={12} weight="bold" />
        {label}
      </div>
      <div className="text-sm text-ink-900 leading-relaxed break-words">
        {value || <span className="text-warm-400">—</span>}
      </div>
    </div>
  );
}

function PasswordChangeCard() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setOldPw("");
    setNewPw("");
    setConfirmPw("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) {
      setError("Das neue Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("Die beiden neuen Passwörter stimmen nicht überein.");
      return;
    }
    if (newPw === oldPw) {
      setError("Das neue Passwort muss sich vom alten unterscheiden.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/me/change-password", {
        old_password: oldPw,
        new_password: newPw,
      });
      reset();
      toast.success("Passwort erfolgreich geändert.");
    } catch (err) {
      setError(extractError(err, "Passwort konnte nicht geändert werden."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-ink-200 bg-white rounded-sm relative" data-testid="profile-pw-card">
      <div className="absolute top-0 left-0 h-1 w-20 bg-brand-400" aria-hidden="true" />
      <div className="px-6 py-6 border-b border-ink-200">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
          Sicherheit
        </div>
        <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium">
          Passwort ändern
        </h2>
        <p className="mt-1 text-xs text-warm-600">
          Aktuelles Passwort zur Bestätigung eingeben, dann zweimal das neue Passwort.
        </p>
      </div>

      <form className="px-6 py-6 space-y-4" onSubmit={handleSubmit} noValidate>
        <div>
          <Label htmlFor="old-pw">Aktuelles Passwort</Label>
          <div className="relative">
            <Lock
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none"
            />
            <Input
              id="old-pw"
              type="password"
              autoComplete="current-password"
              required
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              className="pl-9"
              data-testid="profile-old-pw"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="new-pw">Neues Passwort</Label>
            <div className="relative">
              <Lock
                size={16}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none"
              />
              <Input
                id="new-pw"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={128}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="mindestens 8 Zeichen"
                className="pl-9"
                data-testid="profile-new-pw"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="confirm-pw">Bestätigen</Label>
            <div className="relative">
              <Lock
                size={16}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none"
              />
              <Input
                id="confirm-pw"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={128}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="pl-9"
                data-testid="profile-confirm-pw"
              />
            </div>
          </div>
        </div>

        {error && (
          <div
            data-testid="profile-pw-error"
            role="alert"
            className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
          >
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button
            type="submit"
            variant="brand"
            size="md"
            disabled={submitting || !oldPw || !newPw || !confirmPw}
            data-testid="profile-pw-submit"
          >
            {submitting ? "Wird gespeichert…" : "Passwort ändern"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  if (!user) return null;

  const statusLabel = user.status === "aktiv" ? "Aktiv" : user.status === "ausstehend" ? "Ausstehend" : "Abgelehnt";
  const roleLabel =
    user.role === "superadmin"
      ? "Superadmin"
      : user.role === "admin"
        ? "Administrator"
        : "Benutzer";

  return (
    <UserLayout title="Profil" subtitle="Ihre Konto-Informationen.">
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl space-y-8" data-testid="profile-page">
        <section className="border border-ink-200 bg-white rounded-sm relative" data-testid="profile-info-card">
          <div className="absolute top-0 left-0 h-1 w-20 bg-ink-900" aria-hidden="true" />
          <div className="px-6 py-6 border-b border-ink-200 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-ink-200 bg-warm-50">
              <UserCircle size={24} weight="duotone" className="text-ink-900" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
                Konto-Übersicht
              </div>
              <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
                {user.name || "Unbenannt"}
              </h2>
              <p className="mt-1 text-sm text-warm-700">{user.email}</p>
            </div>
          </div>

          <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ReadOnlyField
              icon={UserCircle}
              label="Name"
              value={user.name}
              testId="profile-field-name"
            />
            <ReadOnlyField
              icon={Buildings}
              label="Firma"
              value={user.company}
              testId="profile-field-company"
            />
            <ReadOnlyField
              icon={EnvelopeSimple}
              label="E-Mail"
              value={user.email}
              testId="profile-field-email"
            />
            <ReadOnlyField
              icon={ShieldCheck}
              label="Rolle"
              value={roleLabel}
              testId="profile-field-role"
            />
            <ReadOnlyField
              icon={CheckCircle}
              label="Status"
              value={statusLabel}
              testId="profile-field-status"
            />
            <ReadOnlyField
              icon={Calendar}
              label="Konto erstellt"
              value={formatDate(user.created_at)}
              testId="profile-field-created"
            />
          </div>

          <div className="px-6 py-4 bg-warm-50 border-t border-ink-200 text-xs text-warm-600 leading-relaxed">
            Stammdaten können aktuell nur über den Administrator angepasst werden.
            Sie möchten eine Änderung? Schreiben Sie uns einfach eine kurze E-Mail.
          </div>
        </section>

        <PasswordChangeCard />
      </div>
    </UserLayout>
  );
}
